import { ApiError, Button, Field, Loading, Note, useToast, ErrorNote } from "@corecrm/ui";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useJoin, useJoinDetails, useRequestNewJoinLink } from "../api/queries";
import type { JoinDetails } from "../api/types";
import { PublicShell } from "../components/bits";
import { MIN_PASSWORD } from "./SignIn";

/* /join/<token>: the sign-up link in a quote, change-request or invite email (TDD §8). Confirm
   name and email (pre-filled, editable because emails get forwarded), pick a sign-in method, and
   land on the quote. A changed email is a forwarded link: that person becomes a new contact, is
   asked whether they handle billing, and verifies the address from a sign-in link. */
export function JoinPage() {
  const { token = "" } = useParams();
  const details = useJoinDetails(token);
  if (details.isPending)
    return (
      <PublicShell title="Create your login">
        <Loading />
      </PublicShell>
    );
  if (details.error) return <DeadLink token={token} error={details.error} />;
  return <JoinForm token={token} details={details.data} />;
}

export function splitName(full: string): { first_name: string; last_name: string | null } {
  const parts = full.trim().split(/\s+/);
  const first = parts.shift() ?? "";
  return { first_name: first, last_name: parts.length ? parts.join(" ") : null };
}

function JoinForm({ token, details: d }: { token: string; details: JoinDetails }) {
  const join = useJoin();
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState([d.first_name, d.last_name].filter(Boolean).join(" "));
  const [email, setEmail] = useState(d.email);
  const [method, setMethod] = useState<"password" | "email_link">("password");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<"details" | "billing">("details");
  const forwarded = email.trim().toLowerCase() !== d.email.toLowerCase();
  const ready = name.trim() && email.trim() && (method !== "password" || password.length >= MIN_PASSWORD);

  const submit = (billing_contact: boolean) =>
    join.mutate(
      {
        token,
        ...splitName(name),
        email: email.trim(),
        sign_in_method: method,
        password: method === "password" ? password : null,
        billing_contact,
      },
      {
        onSuccess: (r) => {
          if (r.status === "signed_in") {
            toast(`Welcome, ${splitName(name).first_name}. Your portal login is ready.`);
            navigate(r.quote_id ? `/quotes/${r.quote_id}` : "/", { replace: true });
          }
        },
      },
    );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  if (join.data?.status === "verify_email")
    return (
      <PublicShell title="Check your email">
        <h1>Check your email</h1>
        <p className="muted">
          We sent a sign-in link to <strong>{join.data.verification_sent_to ?? email}</strong>. Open it to
          verify the address and finish setting up your login.
        </p>
      </PublicShell>
    );

  if (step === "billing")
    return (
      <PublicShell title="Billing contact">
        <h1 id="join-billing">Are you a billing contact for {d.account_name}?</h1>
        <p className="muted">
          Billing contacts get quotes and invoices by email, and see invoices, quotes and amounts in the
          portal. Unless the person who sent you this link is a billing contact, CoreNode confirms it first.
          You can change this later by asking CoreNode.
        </p>
        <div className="note">
          You'll sign in as <strong>{name}</strong> ({email}), a new contact at {d.account_name}. The person
          this link was sent to keeps their own details. We'll send a sign-in link to {email} to verify the
          address.
        </div>
        {join.error && <ErrorNote error={join.error} />}
        <div className="join-yn">
          <Button variant="primary" busy={join.isPending} onClick={() => submit(true)}>
            Yes, I handle billing
          </Button>
          <Button busy={join.isPending} onClick={() => submit(false)}>
            No
          </Button>
        </div>
        <Button variant="ghost" onClick={() => setStep("details")}>
          Back to my details
        </Button>
      </PublicShell>
    );

  return (
    <PublicShell title="Create your login">
      <h1 id="join-confirm">Confirm your details</h1>
      <p className="muted">
        {d.account_name} has {d.quote_id ? "a quote waiting for you" : "a client portal waiting for you"}.
        Check your name and email, then create your login.
      </p>
      <form
        className="stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (forwarded) setStep("billing");
          else submit(false);
        }}
      >
        <Field label="Full name" hint="Fix the spelling here if it's wrong.">
          <input type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field
          label="Email"
          hint={
            forwarded
              ? `This isn't the address the link was sent to. We'll set you up as a new contact at ${d.account_name} and send a sign-in link to verify it.`
              : "This link was emailed to this address, so it's already verified."
          }
        >
          <input
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <fieldset className="grp">
          <legend>How do you want to sign in?</legend>
          <label className="radio">
            <input
              type="radio"
              name="method"
              checked={method === "password"}
              onChange={() => setMethod("password")}
            />
            <span>
              <strong>Set a password</strong>
              <small>At least {MIN_PASSWORD} characters</small>
            </span>
          </label>
          {method === "password" && (
            <Field
              label="Password"
              error={
                password && password.length < MIN_PASSWORD ? `At least ${MIN_PASSWORD} characters.` : null
              }
            >
              <input
                type="password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          )}
          <label className="radio">
            <input
              type="radio"
              name="method"
              checked={method === "email_link"}
              onChange={() => setMethod("email_link")}
            />
            <span>
              <strong>Email me a one-time sign-in link each time</strong>
              <small>No password to remember.</small>
            </span>
          </label>
        </fieldset>
        {join.error && <ErrorNote error={join.error} />}
        <div className="row">
          <Button type="submit" variant="primary" busy={join.isPending} disabled={!ready}>
            {forwarded ? "Continue" : "Create my login"}
          </Button>
        </div>
      </form>
      <p className="faint">
        Already have a login? <Link to="/sign-in">Sign in</Link>
      </p>
    </PublicShell>
  );
}

/** A used, expired or mangled link. "Request a new link" emails the original contact. */
function DeadLink({ token, error }: { token: string; error: unknown }) {
  const resend = useRequestNewJoinLink();
  const invalid = error instanceof ApiError && error.status === 400;
  if (!invalid)
    return (
      <PublicShell title="Create your login">
        <h1>We couldn't open this link</h1>
        <ErrorNote error={error} />
      </PublicShell>
    );
  return (
    <PublicShell title="Link expired">
      <h1 id="join-dead">This link has expired or was already used</h1>
      <p className="muted">
        Sign-up links work once and last 7 days. We can email a new link to the person it was sent to.
      </p>
      {resend.isSuccess ? (
        <Note kind="acc">If this link is ours, a new one is on its way. Check your email.</Note>
      ) : (
        <div className="row">
          <Button variant="primary" busy={resend.isPending} onClick={() => resend.mutate(token)}>
            Request a new link
          </Button>
          <Link className="btn" to="/sign-in">
            Sign in
          </Link>
        </div>
      )}
    </PublicShell>
  );
}
